import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import queryString from 'query-string';
import { useHistory } from 'react-router-dom';
import clone from 'lodash/clone';
import T from 'i18n-react';
import Notification from 'components/Notification';
import ViewMode from 'components/New/ViewMode';
import { VIEW_MODE } from 'components/New/ViewMode/constants';
import ATabContent from 'components/Antd/Tabs/TabContent';
import NFilterBar from 'components/New/FilterBar';
import ABadge from 'components/Antd/Badge';
import ATabs from 'components/Antd/Tabs';
import { useDocumentManagementFiltersDropdown } from 'hooks';
import { ITEM_TYPE } from 'views/DocumentManagement/components/FunctionBar/constants';
import Jobs from '../components/Jobs';
import VerifyModal from '../components/Modal/VerifyModal';
import { useFunctionBarActions } from './useFunctionBarActions';
import classes from './DocumentsTab.module.scss';
import { useActions, useIndexData } from './selectorData';
import { TAB_KEYS, DEFAULT_FILTERS, TAB_LABELS, TAB_FUNCTION_ITEMS } from './constants';
import { getFilterItems, getFilterParamsFn } from './utils';

const DROPZONE_ID = 'product-profile-document-droparea';

const DocumentsTab = ({
  productId,
  selectedRowKeys,
  setSelectedRowKeys,
  selectedRows,
  setSelectedRows,
  currentTabKey,
  setCurrentTabKey,
  onSelectFolder,
  selectedFolders,
  onReloadCb,
  defaultSelectedFolders,
  loadExpiredFiles,
  onDropFiles,
}) => {
  const history = useHistory();
  const queryParams = queryString.parse(history?.location?.search);
  const { typeOptions, sourceOptions } = useDocumentManagementFiltersDropdown();
  const [currentFilters, setCurrentFilters] = useState(DEFAULT_FILTERS);
  const [searchVal, setSearchVal] = useState('');
  const [currentMode, setCurrentMode] = useState(VIEW_MODE.FOLDER);
  const [notification, setNotification] = useState({ type: null, message: null });
  const [verifyModal, setVerifyModal] = useState({
    open: false,
    confirm: () => {},
  });
  const { getProductAllFiles, getProductArchivedFiles, getProductAllFiles1 } = useActions();
  const { allDocuments, archivedDocuments } = useIndexData();
  const loadJobActionMapping = {
    [TAB_KEYS.ALL_JOB]: getProductAllFiles,
    [TAB_KEYS.ARCHIVED]: getProductArchivedFiles,
  };
  const tabDataMapping = {
    [TAB_KEYS.ALL_JOB]: allDocuments,
    [TAB_KEYS.ARCHIVED]: archivedDocuments,
  };
  const thisTabData = tabDataMapping[currentTabKey];

  const loadJobs = (tPage, tSize, tSearch, filtersParam, tTabKey, tViewMode, cb) => {
    const tCurrentTabKey = tTabKey || currentTabKey;
    const tFilters = getFilterParamsFn({
      filtersParam,
      page: tPage,
      size: tSize,
      search: tSearch,
      currentTab: tCurrentTabKey,
      viewMode: tCurrentTabKey === TAB_KEYS.ARCHIVED ? 'document' : tViewMode || currentMode,
    });
    const tSelectedFolders = selectedFolders[tCurrentTabKey] || [];
    const lastSelectedFolder = tSelectedFolders?.[tSelectedFolders.length - 1] || null;
    if (lastSelectedFolder?.id) {
      tFilters.parentId = lastSelectedFolder.id;
    }
    const tAction = loadJobActionMapping[tCurrentTabKey];
    if (tAction) {
      if (onReloadCb) {
        onReloadCb(tCurrentTabKey, tAction, tFilters);
      }
      tAction(productId, tFilters, cb);
    }
    if (loadExpiredFiles) loadExpiredFiles();
  };
  const tFilterItemsValue = getFilterItems({
    filters: currentFilters,
    typeOptions,
    sourceOptions,
    T,
  });
  const filterMapping = {};
  Object.keys(TAB_KEYS).forEach((key) => {
    const tKeyValue = TAB_KEYS[key];
    filterMapping[tKeyValue] = tFilterItemsValue?.[tKeyValue];
  });
  const reloadCurrentTabData = (tPage) => {
    Object.keys(TAB_KEYS).forEach((tKey) => {
      const tTabKey = TAB_KEYS[tKey];
      const jobObj = tabDataMapping[tTabKey];
      loadJobs(
        tPage || jobObj?.pagination?.page,
        jobObj?.pagination?.pageSize,
        searchVal,
        currentFilters[tTabKey],
        tTabKey
      );
    });
  };
  const reloadSelectedKeys = () => {
    handleSelectedRowKeysChanged(currentTabKey)([]);
    handleSelectedRowsChanged(currentTabKey)([]);
  };
  const {
    handleDownloadDocuments,
    handleActionDocuments,
    handleRenameDocuments,
    handleEditTypeDocuments,
    handleSetDatesDocuments,
    handleShareDocuments,
    handleShareDocumentsEmail,
    handleVerifyDocument,
    handleDeleteDocuments,
    handleMoveDocuments,
    handleSetDataDocuments,
    handleUpdateDocument,
  } = useFunctionBarActions(setNotification, reloadCurrentTabData, reloadSelectedKeys);
  const updateFilter = (newFilter) => {
    setCurrentFilters(newFilter);
    setTimeout(() => {
      loadJobs(1, 10, searchVal, newFilter[currentTabKey]);
    });
  };
  const handleFilterApply = (field, val, dateVal) => {
    const tKey = field.key;
    const newFilters = {
      ...currentFilters,
      [currentTabKey]: {
        ...currentFilters[currentTabKey],
        [tKey]:
          tKey !== 'uploadedDate'
            ? val
            : {
                ...val,
                dates: dateVal,
              },
      },
    };
    if (tKey === 'size') {
      newFilters[currentTabKey].uploadedDate = {
        dates: [],
        field: '',
        method: '',
      };
    } else if (tKey === 'uploadedDate') {
      newFilters[currentTabKey].size = {
        method: '',
      };
    }
    updateFilter(newFilters);
  };
  const handleFilterReset = (field, val) => {
    const tKey = field.key;
    const newFilters = {
      ...currentFilters,
      [currentTabKey]: {
        ...currentFilters[currentTabKey],
        [tKey]: clone(DEFAULT_FILTERS[currentTabKey])[tKey],
      },
    };
    updateFilter(newFilters);
  };
  const handleFilterBarReset = () => {
    const newFilters = {
      ...currentFilters,
      [currentTabKey]: {
        ...clone(DEFAULT_FILTERS[currentTabKey]),
      },
    };
    updateFilter(newFilters);
  };
  const handleSelectedRowKeysChanged = (tTabKey) => (tVal) => {
    setSelectedRowKeys({
      ...selectedRowKeys,
      [tTabKey]: tVal,
    });
  };
  const handleSelectedRowsChanged = (tTabKey) => (tVal) => {
    setSelectedRows({
      ...selectedRows,
      [tTabKey]: tVal,
    });
  };
  const onReview = (obj) => {
    setVerifyModal({
      ...verifyModal,
      open: true,
      confirm: (notes, status, cb) => {
        handleVerifyDocument(productId, obj.originalId, status, notes, () => {
          setVerifyModal({ ...verifyModal, open: false });
          if (cb) cb();
        });
      },
    });
  };

  const onUpdateDocument = (_record, values, form, cb) => {
    handleUpdateDocument(values, form, cb);
  };

  const items = Object.keys(TAB_KEYS).map((tKey) => {
    const thisKeyValue = TAB_KEYS[tKey];
    const isAllJobTab = thisKeyValue === TAB_KEYS.ALL_JOB;
    const filterData = filterMapping[thisKeyValue];
    const jobObj = tabDataMapping[thisKeyValue];
    const tSelectedRowKeys = selectedRowKeys[thisKeyValue] || [];
    const tSelectedRows = selectedRows[thisKeyValue] || [];
    const tFunctionBarItems = TAB_FUNCTION_ITEMS[thisKeyValue] || [];
    const tSelectedFolders = selectedFolders[thisKeyValue] || [];
    const lastSelectedFolder = tSelectedFolders?.[tSelectedFolders.length - 1] || null;
    return {
      key: thisKeyValue,
      label: (
        <>
          <span>{T.translate(`documentManagement.text.${TAB_LABELS[thisKeyValue]}`)}</span>
          <ABadge count={jobObj?.pagination?.total} overflowCount={10000} />
        </>
      ),
      children: (
        <ATabContent>
          {tSelectedRows.length === 0 && (
            <>
              {thisKeyValue !== TAB_KEYS.ARCHIVED && (
                <ViewMode
                  currentMode={currentMode}
                  onChange={(val) => {
                    setCurrentMode(val);
                    onSelectFolder(null, [], val);
                  }}
                  className={classes.viewModeWrapper}
                />
              )}
              <NFilterBar
                items={filterData}
                onApply={handleFilterApply}
                onReset={handleFilterReset}
                onFilterReset={handleFilterBarReset}
                className={clsx(classes.jobFilterBar, {
                  [classes.noViewMode]: thisKeyValue === TAB_KEYS.ARCHIVED,
                })}
              />
            </>
          )}
          <div className={classes.tableWrapper}>
            <Jobs
              loadJobs={(pagination, filters, sorter, extra) => {
                let tPage = pagination.current || 1;
                let tPageSize = pagination.pageSize || 10;
                if (thisTabData?.pagination?.pageSize !== pagination.pageSize) {
                  tPage = 1;
                }
                loadJobs(tPage, tPageSize, searchVal, currentFilters[thisKeyValue]);
              }}
              jobObj={jobObj}
              isFolder={currentMode === VIEW_MODE.FOLDER}
              isArchive={currentTabKey === TAB_KEYS.ARCHIVED}
              selectedRowKeys={tSelectedRowKeys}
              selectedRows={tSelectedRows}
              setSelectedRowKeys={handleSelectedRowKeysChanged(thisKeyValue)}
              setSelectedRows={handleSelectedRowsChanged(thisKeyValue)}
              functionBarItems={tFunctionBarItems}
              typeOptions={typeOptions}
              onReview={onReview}
              onUpdateDocument={onUpdateDocument}
              actions={{
                [ITEM_TYPE.ARCHIVE]: handleActionDocuments(tSelectedRows, 'archive'),
                [ITEM_TYPE.UN_ARCHIVE]: handleActionDocuments(tSelectedRows, 'unarchive'),
                [ITEM_TYPE.REMOVE]: handleDeleteDocuments(tSelectedRows, productId),
                [ITEM_TYPE.DOWNLOAD]: handleDownloadDocuments(tSelectedRows),
                [ITEM_TYPE.RENAME]: handleRenameDocuments(tSelectedRows),
                [ITEM_TYPE.EDIT_TYPE]: handleEditTypeDocuments(tSelectedRows),
                [ITEM_TYPE.SET_DATES]: handleSetDatesDocuments(tSelectedRows),
                [ITEM_TYPE.SHARE]: handleShareDocuments(tSelectedRows, productId),
                [ITEM_TYPE.SHARE_EMAIL]: handleShareDocumentsEmail(tSelectedRows, productId),
                [ITEM_TYPE.MOVE]: handleMoveDocuments(tSelectedRows, productId),
                [ITEM_TYPE.SET_DATA]: handleSetDataDocuments(tSelectedRows),
              }}
              onSelectFolder={onSelectFolder}
              selectedFolder={lastSelectedFolder}
              isArchivedTab={currentTabKey === TAB_KEYS.ARCHIVED}
              defaultSelectedFolders={defaultSelectedFolders}
              onLoadFiles={(tFolderId, cb) => {
                const tFilters = getFilterParamsFn({
                  page: 1,
                  size: 9999,
                  currentTab: TAB_KEYS.ALL_JOB,
                });
                getProductAllFiles1(productId, { ...tFilters, parentId: tFolderId }, cb);
              }}
            />
            {isAllJobTab && (
              <div className={classes.droparea} id={DROPZONE_ID}>
                <span>Drag file heres to upload</span>
              </div>
            )}
          </div>
        </ATabContent>
      ),
    };
  });
  function preventDefaults(e) {
    e.preventDefault();
    e.stopPropagation();
  }
  const toggleDropzone = (isShow) => {
    const droparea = document.getElementById(DROPZONE_ID);
    if (droparea) {
      if (isShow) {
        droparea.classList.add('show');
      } else {
        droparea.classList.remove('show');
      }
    }
  };
  function dragOver() {
    toggleDropzone(true);
  }
  function dragLeave(e) {
    if (e.screenX === 0 && e.screenY === 0) {
      toggleDropzone();
    }
  }
  const bodyDragEnd = () => {
    toggleDropzone();
  };
  const startUpload = (e) => {
    e.preventDefault();
    const files = e.dataTransfer.files;
    if (files.length > 0 && onDropFiles) {
      onDropFiles(
        Object.keys(files).map((key) => {
          const tFile = files[key];
          tFile.originFileObj = tFile;
          return tFile;
        })
      );
    }
    toggleDropzone();
  };

  useEffect(() => {
    Object.keys(TAB_KEYS).forEach((tKey) => {
      loadJobs(1, 10, '', currentFilters[TAB_KEYS[tKey]], TAB_KEYS[tKey]);
    });
    document.body.addEventListener('dragenter', dragOver);
    document.body.addEventListener('dragover', preventDefaults);
    document.body.addEventListener('drop', bodyDragEnd);
    document.body.addEventListener('dragleave', dragLeave);
    const droparea = document.getElementById(DROPZONE_ID);
    if (droparea) {
      droparea.addEventListener('drop', startUpload);
    }
    return () => {
      document.body.removeEventListener('dragenter', dragOver);
      document.body.removeEventListener('dragover', preventDefaults);
      document.body.removeEventListener('drop', bodyDragEnd);
      document.body.removeEventListener('dragleave', dragLeave);
      const droparea = document.getElementById(DROPZONE_ID);
      if (droparea) {
        droparea.removeEventListener('drop', startUpload);
      }
    };
  }, []);

  return (
    <React.Fragment>
      <ATabs
        items={items}
        activeKey={currentTabKey}
        onChange={(tTab) => {
          setCurrentTabKey(tTab);
          loadJobs(1, 10, searchVal, currentFilters[tTab], tTab);
        }}
      />
      {!!verifyModal.open && (
        <VerifyModal
          open={true}
          handleClose={(reset) => {
            setVerifyModal({ open: false });
            reset();
          }}
          handleSubmit={verifyModal.confirm}
        />
      )}
      <Notification
        notification={notification}
        onClose={() => setNotification({ type: null, message: null })}
        handleOnClose={() => setNotification({ type: null, message: null })}
      />
    </React.Fragment>
  );
};

export default DocumentsTab;
